import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Input, Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError, api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { COUNTRIES } from '../../utils/countries.js';
import {
  type IdentityNumberValue,
  formatMykad,
  identityNumberBody,
  identityNumberError,
} from '../../utils/identityNumber.js';

/**
 * 366 (#1223, US3, design 1g) — staff change or clear a customer's IC number, with a reason (recorded in the
 * customer's history, never the number). A clash names the account that holds it, to staff only, so the right one can
 * be fixed first. Clearing it asks the customer again on their next visit.
 */
export function IdentityNumberDialog({
  userId,
  hasNumber,
  onClose,
}: {
  userId: string;
  hasNumber: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<IdentityNumberValue>({ kind: 'mykad', number: '' });
  const [reason, setReason] = useState('');
  const numberId = useId();
  const reasonId = useId();
  const countryId = useId();

  const save = useMutation({
    mutationFn: (identity: IdentityNumberValue | null) =>
      api.patch(`/users/${userId}/identity-number`, {
        identity_number: identity ? identityNumberBody(identity) : null,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(userId) }),
        queryClient.invalidateQueries({ queryKey: ['user-events', userId] }),
      ]);
      onClose();
    },
  });

  const numberError = value.number ? identityNumberError(value) : null;
  const reasonOk = reason.trim().length >= 3;
  const canSave = !save.isPending && reasonOk && value.number !== '' && !numberError;
  const holder =
    save.error instanceof ApiError && save.error.code === 'IDENTITY_NUMBER_TAKEN'
      ? (save.error.details.holder as { _id: string; name: string } | null | undefined)
      : undefined;

  const choose = (kind: IdentityNumberValue['kind']) =>
    setValue(kind === 'mykad' ? { kind, number: '' } : { kind, country: '', number: '' });

  return (
    <Modal
      open
      onClose={onClose}
      title="Change IC number"
      footer={
        <div className="flex items-center justify-between gap-2">
          {hasNumber ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={!reasonOk || save.isPending}
              onClick={() => save.mutate(null)}
            >
              Clear the number
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSave}
              loading={save.isPending}
              onClick={() => save.mutate(value)}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex gap-4 text-sm">
          <legend className="sr-only">Identity document</legend>
          {(['mykad', 'passport'] as const).map((kind) => (
            <label key={kind} className="flex items-center gap-1.5">
              <input
                type="radio"
                name="identity_kind"
                checked={value.kind === kind}
                onChange={() => choose(kind)}
              />
              {kind === 'mykad' ? 'MyKad' : 'Passport'}
            </label>
          ))}
        </fieldset>
        {value.kind === 'passport' && (
          <div className="flex flex-col gap-1">
            <label htmlFor={countryId} className="text-sm font-medium text-ink">
              Country
            </label>
            <Select
              id={countryId}
              value={value.country ?? ''}
              onChange={(e) => setValue({ ...value, country: e.target.value })}
            >
              <option value="">Choose…</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor={numberId} className="text-sm font-medium text-ink">
            Number
          </label>
          <Input
            id={numberId}
            className="font-mono"
            autoComplete="off"
            value={value.number}
            onChange={(e) =>
              setValue({
                ...value,
                number: value.kind === 'mykad' ? formatMykad(e.target.value) : e.target.value,
              })
            }
          />
          {numberError && <p className="text-sm text-semantic-danger-text">{numberError}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={reasonId} className="text-sm font-medium text-ink">
            Reason
          </label>
          <Textarea
            id={reasonId}
            rows={2}
            placeholder="e.g. customer mistyped one digit"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {save.isError && (
          <p role="alert" className="text-sm text-semantic-danger-text">
            {holder ? (
              <>
                Already registered to{' '}
                <Link to={`/users/${holder._id}`} className="font-medium text-primary underline">
                  {holder.name}
                </Link>
                . Change or clear that account's number first.
              </>
            ) : (
              ((save.error as { message?: string }).message ?? 'Could not change the IC number.')
            )}
          </p>
        )}
      </div>
    </Modal>
  );
}
